import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import {
  cancelPlan,
  checkActivePlan,
  getAccountStats,
  getBillCount,
  getCurrentPlan,
  getInvoicesData,
  getUpdatePrimaryCard,
} from "./PlanService";
import {
  deleteCard,
  fetchPlansCatalog,
  fetchSubscriptionState,
  getCards,
} from "Services/CommonServices";
import { groupPlansByCycle } from "./components/UpgradePlanHelper";
import { store } from "utils/store";
import strings from "global/constants/StringConstants";
import { getPdfSignRequestCount } from "screens/MySignRequests/MySignRequestService";
import { cardNetworks } from "global/constants/ConstantObject";
import visa from "assets/images/visa.png";
import mastercard from "assets/images/mastercard.png";
import americanExpress from "assets/images/americanExpress.png";
import chinesUnionPay from "assets/images/chinesUnionPay.jpg";
import cartesBancaires from "assets/images/cartesBancaires.png";
import dinersClub from "assets/images/dinersClub.png";
import jcb from "assets/images/jcb.png";
import discover from "assets/images/discover.png";

/**
 * Plan card + account stats + subscription state + saved cards — everything
 * on this screen *except* billing history, which has its own dedicated fetch
 * (`getInovices`/the page's `fetchData`) driven by its own loading flag and
 * respecting whatever page the customer has open. Bundling an invoice fetch
 * in here too used to silently reset that table back to page 1 on every
 * call, with no skeleton of its own — call both alongside each other instead.
 */
export const getCurrentPlanAndBillingData = async (
  setLoading: Function,
  setCardData: Function,
  setIsSubscriptionActive: Function,
  setAccountStats: Function,
  setCurrentPlan: Function,
  setPrimaryPaymentID: Function,
  setPaymentID: any,
  setSubscriptionState?: Function
) => {
  try {
    setLoading(true);
    const accountName = store.getState().auth.account;
    const [cards, activePlan, accountStats, currentPlan, subscriptionState] =
      await Promise.all([
        getCardDetails(
          setCardData,
          setLoading,
          setPrimaryPaymentID,
          setPaymentID
        ),
        checkActivePlan(),
        getAccountStats(),
        getCurrentPlan(accountName),
        fetchSubscriptionState(),
      ]);
    setIsSubscriptionActive(activePlan);
    setAccountStats(accountStats);
    setCurrentPlan(currentPlan);
    setSubscriptionState?.(subscriptionState);
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const getCardDetails = async (
  setCardData: Function,
  setLoading: Function,
  setPrimaryPaymentID: any,
  setPaymentID: any
) => {
  try {
    const CardData = await getCards();
    const primaryCardDetails = CardData.filter((x: any) => x.default);
    if (primaryCardDetails.length > 0) {
      setPrimaryPaymentID(primaryCardDetails[0].id);
      setPaymentID(primaryCardDetails[0].id);
    }
    setCardData(CardData);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const deleteCardDetails = async (
  setCardData: any,
  setLoading: Function,
  deleteCardId: any,
  setPrimaryPaymentID: any,
  setPaymentID: any
) => {
  try {
    setLoading(true);
    const cardRespone = await deleteCard(deleteCardId);
    await getCardDetails(
      setCardData,
      setLoading,
      setPrimaryPaymentID,
      setPaymentID
    );
    setLoading(false);
    return cardRespone;
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const getInovices = async (
  pageNumber: number,
  setInvoiceData: Function,
  convertDataToTableFormat: Function,
  setCount: Function,
  setLoading: Function
) => {
  try {
    setLoading(true);
    const [invoiceData, billCount] = await Promise.all([
      getInvoicesData(pageNumber, strings.LIMIT_PAGE),
      getBillCount(),
    ]);
    const tableData = convertDataToTableFormat(invoiceData);
    setInvoiceData(tableData);
    setCount(billCount);
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const handleTableCount = async (
  setCount: Function,
  setLoading: Function
) => {
  try {
    setLoading(true);
    const count = await getPdfSignRequestCount("All", "");
    setCount(count);
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setLoading(false);
  }
};

export const getAllPlansData = async (
  setAllPlansData: Function,
  setLoading: Function
) => {
  try {
    setLoading(true);
    // Auth state carries no app name, so the product constant is the source.
    const appName = store.getState().auth.app || strings.inkfree;
    const catalog = await fetchPlansCatalog(appName);
    setAllPlansData(groupPlansByCycle(catalog));
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};
export const updatePrimaryCard = async (
  setLoading: Function,
  setCardData: Function,
  paymentId: String,
  setPrimaryPaymentID: Function,
  setPaymentID: Function
) => {
  try {
    setLoading(true);
    const updateCard = await getUpdatePrimaryCard(paymentId);
    await getCardDetails(
      setCardData,
      setLoading,
      setPrimaryPaymentID,
      setPaymentID
    );
    openSuccessNotification("Successfully updated default card details.");
    setLoading(false);
    return updateCard;
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const cancelSubscription = async (
  setLoading: Function,
  setCancelSubscriptionPopup: Function,
  getUserData: Function
) => {
  try {
    setLoading(true);
    await cancelPlan();
    // Keep the confirmation dialog open (with its busy spinner, driven by the
    // same `setLoading`) through the full refetch — closing it right after
    // `cancelPlan` resolves would drop the customer back onto a plan card
    // that's still showing the just-cancelled plan for however long the
    // refetch takes.
    await getUserData();
    setCancelSubscriptionPopup(false);
    openSuccessNotification("your subscription is cancelled.");
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    setCancelSubscriptionPopup(false);
    handleErrorMessage(error);
  }
};

export const getCardCompany = (cardName: string) => {
  switch (cardName) {
    case cardNetworks.visa.toLocaleLowerCase():
      return visa;
    case cardNetworks.mastercard.toLocaleLowerCase():
      return mastercard;
    case cardNetworks.americanExpress.toLocaleLowerCase():
      return americanExpress;
    case cardNetworks.ChinaUnionPay.toLocaleLowerCase():
      return chinesUnionPay;
    case cardNetworks.cartesBancaires.toLocaleLowerCase():
      return cartesBancaires;
    case cardNetworks.dinersClub.toLocaleLowerCase():
      return dinersClub;
    case cardNetworks.jcb.toLocaleLowerCase():
      return jcb;
    case cardNetworks.discover.toLocaleLowerCase():
      return discover;
  }
};
